/**
 * SQL parsing utilities for extracting table names from queries.
 *
 * Centralizes regex patterns for INSERT, UPDATE, DELETE, SELECT queries.
 * Used by query-interceptor.ts and database.ts for CRUD event detection
 * and SQLite boolean conversion.
 */

type SqlOperationType = 'select' | 'insert' | 'update' | 'delete'

const SQL_PATTERNS: Record<SqlOperationType, RegExp> = {
  select: /from\s+["'`]?(\w+)["'`]?/i,
  insert: /insert into\s+["'`]?(\w+)["'`]?/i,
  update: /update\s+["'`]?(\w+)["'`]?/i,
  delete: /delete from\s+["'`]?(\w+)["'`]?/i
}

/**
 * Extracts the table name from a SQL query based on operation type.
 */
export function extractTableFromSql(sql: string, type: SqlOperationType): string | undefined {
  const match = sql.match(SQL_PATTERNS[type])
  return match?.[1]
}

/**
 * Extracts table name from a SELECT query.
 * @example extractTableFromSelect("SELECT * FROM users WHERE id = 1") // "users"
 */
export function extractTableFromSelect(sql: string): string | undefined {
  return extractTableFromSql(sql, 'select')
}

/**
 * Extracts table name from an INSERT query.
 * @example extractTableFromInsert("INSERT INTO users (name) VALUES ('John')") // "users"
 */
export function extractTableFromInsert(sql: string): string | undefined {
  return extractTableFromSql(sql, 'insert')
}

/**
 * Extracts table name from an UPDATE query.
 * @example extractTableFromUpdate("UPDATE users SET name = 'Jane' WHERE id = 1") // "users"
 */
export function extractTableFromUpdate(sql: string): string | undefined {
  return extractTableFromSql(sql, 'update')
}

/**
 * Extracts table name from a DELETE query.
 * @example extractTableFromDelete("DELETE FROM users WHERE id = 1") // "users"
 */
export function extractTableFromDelete(sql: string): string | undefined {
  return extractTableFromSql(sql, 'delete')
}
